import { PhoneCode, phoneCodes } from '@common/constants/phone-codes.constant';
import { InputAddon } from '@common/general-ui/input/input-addon.component';
import { IInputProps } from '@common/general-ui/input/input.component';
import { FullPhone } from '@common/interfaces/full-phone.interface';
import { findLongestSubstring } from '@common/utils/find-phone-code/find-phone-code.util';
import { Select as AntdSelect } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { ReactCountryFlag } from 'react-country-flag';

export const PhoneNumber = (props: Omit<IInputProps, 'type'>): JSX.Element => {
    const [val, setVal] = useState(props.defaultValue || props.value);
    const [selectedCode, setSelectedCode] = useState(
        phoneCodes.find((i) => i.short === window.sessionStorage.getItem('country'))?.phoneCode || '+44',
    );
    const [selectedOption, setSelectedOption] = useState(window.sessionStorage.getItem('country') || 'GB');
    const [prevPhone, setPrevPhone] = useState('');

    useEffect(() => {
        if (!props.value) {
            return;
        }
        let existingPhone = props.value?.toString();
        if (existingPhone?.startsWith('00')) {
            existingPhone = '+' + existingPhone.substring(2);
        }
        if (!existingPhone?.startsWith('+')) {
            existingPhone = '+' + existingPhone;
        }

        let currCodePhone: FullPhone = findLongestSubstring(phoneCodes, existingPhone);
        if (!('phoneCode' in currCodePhone)) {
            currCodePhone = { ...currCodePhone, ...phoneCodes.find((i) => i.short === 'GB') };
        }
        setSelectedCode(currCodePhone.phoneCode || '+44');
        setSelectedOption(currCodePhone.short || 'GB');
        setPrevPhone(currCodePhone.phoneNumber);
        setVal(currCodePhone.phoneCode + currCodePhone.phoneNumber);
    }, [props.value]);

    // eslint-disable-next-line  @typescript-eslint/no-explicit-any
    const handleOptionChange = (value: string, event: any): void => {
        setSelectedOption(value);
        const currentCode = phoneCodes.find((i) => i.short === value)?.phoneCode;
        setSelectedCode(currentCode ?? '44');
        let newValue = currentCode;
        if (val && selectedCode.length < val.toString().length) {
            newValue = currentCode + val.toString().slice(selectedCode.length);
        }
        props.onChange && props.onChange({ ...event, target: { value: newValue } });
        window.sessionStorage.setItem('country', value);
        setVal(newValue);
    };

    const formatValue = useCallback(
        (value: string) => {
            let newValue = '+' + value.replaceAll(/[^0-9]/g, '');
            const codeLength = selectedCode.length;
            if (!newValue.startsWith(selectedCode)) {
                newValue = selectedCode + prevPhone;
            }
            if (newValue.charAt(codeLength) === '0') {
                newValue = newValue.substring(0, codeLength) + newValue.substring(codeLength + 1);
            }
            setPrevPhone(newValue.substring(codeLength));
            return newValue;
        },
        [selectedCode, prevPhone],
    );

    const handleChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setVal(e.target.value);
            props.onChange && props.onChange({ ...e, target: { ...e.target, value: e.target.value } });
        },
        [props],
    );

    const renderOption = (country: PhoneCode): JSX.Element => (
        <div>
            <span>
                <ReactCountryFlag countryCode={country.short ?? ''} svg /> <b>{country.country}</b> ({country.phoneCode})
            </span>
        </div>
    );

    const addonBefore = (
        <AntdSelect
            optionLabelProp="label"
            value={selectedOption}
            onSelect={(value, event): void => handleOptionChange(value, event)}
        >
            {phoneCodes.map((country) => (
                <AntdSelect.Option key={country.short} value={country.short} label={country.short}>
                    {renderOption(country)}
                </AntdSelect.Option>
            ))}
        </AntdSelect>
    );

    return (
        <InputAddon
            {...props}
            type="text"
            addonBefore={addonBefore}
            onChange={handleChange}
            formatValue={formatValue}
            value={val}
        />
    );
};
